import { mapFromKey } from 'constants/ggVars';

import * as React from 'react';

import { useQuery } from '@apollo/client';
import ReportPositiveNegativeDriversQuery from '@graphql/queries/ReportPositiveNegativeDriversQuery';
import ReportStackRankComparisonQuery from '@graphql/queries/ReportStackRankComparisonQuery';
import { Box, useTheme } from '@mui/material';
import LoadingScreen from 'components/LoadingScreen';
import {
  HighlightableTableRow,
  hightlightedConditionalRowStyles,
} from 'components/ReactDataTable/highlightedRowStyles';
import { lineBreak } from 'components/ReactDataTable/reactDataTableUtils';
import { tableCustomStyles } from 'components/ReactDataTable/tableCustomStyles';
import useCustomProductNames from 'hooks/useCustomProductNames';
import useReportSummary, { ProductVersionPq } from 'hooks/useReportSummary';
import DataTable, { TableColumn } from 'react-data-table-component';
import { useTranslation } from 'react-i18next';

import { ComponentTitle } from '../../../../styles/themeComponents';
import { ProductVersion } from '../../ProductVersion';
import { getProductName, isProductHighlighted } from '../../utils';
import { getImpactIcon, ImpactType } from '../utils';

interface ReportProps {
  projectId: number;
  reportId: string;
}

export interface TableRow extends HighlightableTableRow {
  productName: string;
  productId: number;
  version: string;
  WET: ImpactType;
  ROASTED: ImpactType;
  EARTHY: ImpactType;
  ASTRINGENT: ImpactType;
  MARINE: ImpactType;
  MINERAL: ImpactType;
  RETRONASAL: ImpactType;
  GAMEY: ImpactType;
  WOODY: ImpactType;
  BITTER: ImpactType;
  MEATY: ImpactType;
  SMOKED: ImpactType;
  DRY: ImpactType;
  RICH: ImpactType;
  DAIRY: ImpactType;
  MOUTHFEEL: ImpactType;
  NUTS_AND_SEEDS: ImpactType;
  HERBACEOUS: ImpactType;
  COLD_FINISH: ImpactType;
  FLORAL: ImpactType;
  SPICES: ImpactType;
  SOUR_AND_ACIDITY: ImpactType;
  FRUITS: ImpactType;
  SUGAR: ImpactType;
}

const getAILikingDirection = (
  productVersion: ProductVersion,
  reportFlavorStructureResponse: reports.ReportPositiveNegativeDriversResponse,
  key: string,
): ImpactType => {
  const node = (
    reportFlavorStructureResponse?.allRpFlavorStructures?.nodes ?? []
  ).filter((n) => n.product.id === productVersion.productId && n.label === key);

  const nodeColor = node.length > 0 ? node[0].nodeColor : null;
  switch (nodeColor) {
    case 'green':
      return ImpactType.POSITIVE;
    case 'red':
      return ImpactType.NEGATIVE;
    default:
      return ImpactType.NEUTRAL;
  }
};

export const mapToTableRow = (
  productNames,
  reportFlavorStructureResponse: reports.ReportPositiveNegativeDriversResponse,
  productVersionPqs: ProductVersionPq[],
): TableRow[] => {
  return productVersionPqs.map((pn, idx) => ({
    productName: getProductName({
      productNames,
      productId: pn.productVersion.productId,
      version: pn.productVersion.version,
    }),
    productId: pn.productVersion.productId,
    version: pn.productVersion.version,
    isHighlighted: isProductHighlighted({
      productNames,
      productId: pn.productVersion.productId,
      version: pn.productVersion.version,
    }),
    WET: getAILikingDirection(
      pn.productVersion,
      reportFlavorStructureResponse,
      'wet',
    ),
    ROASTED: getAILikingDirection(
      pn.productVersion,
      reportFlavorStructureResponse,
      'roasted',
    ),
    EARTHY: getAILikingDirection(
      pn.productVersion,
      reportFlavorStructureResponse,
      'earthy',
    ),
    ASTRINGENT: getAILikingDirection(
      pn.productVersion,
      reportFlavorStructureResponse,
      'astringent',
    ),
    MARINE: getAILikingDirection(
      pn.productVersion,
      reportFlavorStructureResponse,
      'marine',
    ),
    MINERAL: getAILikingDirection(
      pn.productVersion,
      reportFlavorStructureResponse,
      'mineral',
    ),
    RETRONASAL: getAILikingDirection(
      pn.productVersion,
      reportFlavorStructureResponse,
      'retronasal',
    ),
    GAMEY: getAILikingDirection(
      pn.productVersion,
      reportFlavorStructureResponse,
      'gamey',
    ),
    WOODY: getAILikingDirection(
      pn.productVersion,
      reportFlavorStructureResponse,
      'woody',
    ),
    BITTER: getAILikingDirection(
      pn.productVersion,
      reportFlavorStructureResponse,
      'bitter',
    ),
    MEATY: getAILikingDirection(
      pn.productVersion,
      reportFlavorStructureResponse,
      'meaty',
    ),
    SMOKED: getAILikingDirection(
      pn.productVersion,
      reportFlavorStructureResponse,
      'smoked',
    ),
    DRY: getAILikingDirection(
      pn.productVersion,
      reportFlavorStructureResponse,
      'dry',
    ),
    RICH: getAILikingDirection(
      pn.productVersion,
      reportFlavorStructureResponse,
      'rich',
    ),
    DAIRY: getAILikingDirection(
      pn.productVersion,
      reportFlavorStructureResponse,
      'dairy',
    ),
    MOUTHFEEL: getAILikingDirection(
      pn.productVersion,
      reportFlavorStructureResponse,
      'mouthfeel',
    ),
    NUTS_AND_SEEDS: getAILikingDirection(
      pn.productVersion,
      reportFlavorStructureResponse,
      'nutsAndSeeds',
    ),
    HERBACEOUS: getAILikingDirection(
      pn.productVersion,
      reportFlavorStructureResponse,
      'herbaceous',
    ),
    COLD_FINISH: getAILikingDirection(
      pn.productVersion,
      reportFlavorStructureResponse,
      'coldFinish',
    ),
    FLORAL: getAILikingDirection(
      pn.productVersion,
      reportFlavorStructureResponse,
      'floral',
    ),
    SPICES: getAILikingDirection(
      pn.productVersion,
      reportFlavorStructureResponse,
      'spices',
    ),
    SOUR_AND_ACIDITY: getAILikingDirection(
      pn.productVersion,
      reportFlavorStructureResponse,
      'sourAndAcidity',
    ),
    FRUITS: getAILikingDirection(
      pn.productVersion,
      reportFlavorStructureResponse,
      'fruits',
    ),
    SUGAR: getAILikingDirection(
      pn.productVersion,
      reportFlavorStructureResponse,
      'sugar',
    ),
  }));
};

const CategoryInsightsTable: React.FC<ReportProps> = (props) => {
  const { reportId, projectId } = props;

  const { t } = useTranslation();
  const theme = useTheme();
  const productNames = useCustomProductNames({ projectId: Number(projectId) });

  const columns = (data: TableRow[]): TableColumn<TableRow>[] => {
    const nonNeutralCounts = data.reduce(
      (a, b) => ({
        WET: a.WET + Math.abs(b.WET),
        ROASTED: a.ROASTED + Math.abs(b.ROASTED),
        EARTHY: a.EARTHY + Math.abs(b.EARTHY),
        ASTRINGENT: a.ASTRINGENT + Math.abs(b.ASTRINGENT),
        MARINE: a.MARINE + Math.abs(b.MARINE),
        MINERAL: a.MINERAL + Math.abs(b.MINERAL),
        RETRONASAL: a.RETRONASAL + Math.abs(b.RETRONASAL),
        GAMEY: a.GAMEY + Math.abs(b.GAMEY),
        WOODY: a.WOODY + Math.abs(b.WOODY),
        BITTER: a.BITTER + Math.abs(b.BITTER),
        MEATY: a.MEATY + Math.abs(b.MEATY),
        SMOKED: a.SMOKED + Math.abs(b.SMOKED),
        DRY: a.DRY + Math.abs(b.DRY),
        RICH: a.RICH + Math.abs(b.RICH),
        DAIRY: a.DAIRY + Math.abs(b.DAIRY),
        MOUTHFEEL: a.MOUTHFEEL + Math.abs(b.MOUTHFEEL),
        NUTS_AND_SEEDS: a.NUTS_AND_SEEDS + Math.abs(b.NUTS_AND_SEEDS),
        HERBACEOUS: a.HERBACEOUS + Math.abs(b.HERBACEOUS),
        COLD_FINISH: a.COLD_FINISH + Math.abs(b.COLD_FINISH),
        FLORAL: a.FLORAL + Math.abs(b.FLORAL),
        SPICES: a.SPICES + Math.abs(b.SPICES),
        SOUR_AND_ACIDITY: a.SOUR_AND_ACIDITY + Math.abs(b.SOUR_AND_ACIDITY),
        FRUITS: a.FRUITS + Math.abs(b.FRUITS),
        SUGAR: a.SUGAR + Math.abs(b.SUGAR),
      }),
      {
        WET: 0,
        ROASTED: 0,
        EARTHY: 0,
        ASTRINGENT: 0,
        MARINE: 0,
        MINERAL: 0,
        RETRONASAL: 0,
        GAMEY: 0,
        WOODY: 0,
        BITTER: 0,
        MEATY: 0,
        SMOKED: 0,
        DRY: 0,
        RICH: 0,
        DAIRY: 0,
        MOUTHFEEL: 0,
        NUTS_AND_SEEDS: 0,
        HERBACEOUS: 0,
        COLD_FINISH: 0,
        FLORAL: 0,
        SPICES: 0,
        SOUR_AND_ACIDITY: 0,
        FRUITS: 0,
        SUGAR: 0,
      },
    );

    const nonNeutralGgVars = Object.entries(nonNeutralCounts)
      .filter(([k, v]) => v !== 0)
      .map(([k, x]) => mapFromKey(k));
    const neutralGgVars = Object.entries(nonNeutralCounts)
      .filter(([k, v]) => v === 0)
      .map(([k, x]) => mapFromKey(k));

    return [
      {
        id: 'productName',
        name: lineBreak(t('reports.prodSummary.product')),
        cell: (row: TableRow) => (
          <div style={{ paddingTop: 10, paddingBottom: 10 }}>
            {lineBreak(row.productName)}
          </div>
        ),
        selector: (row: TableRow) => row.productName,
        sortable: true,
      },
    ].concat(
      nonNeutralGgVars
        .concat(neutralGgVars)
        .slice(0, 8)
        .map((columnKey) => ({
          id: columnKey.key,
          width: '9%',
          name: lineBreak(columnKey.label),
          selector: (row: TableRow) => String(row[columnKey.key]),
          cell: (row: TableRow) => getImpactIcon(row[columnKey.key]),
          center: true,
          compact: true,
          sortable: true,
        })),
    );
  };

  const {
    loading: reportFlavorStructureLoading,
    error: reportFlavorStructureError,
    data: reportFlavorStructureResponse,
  } = useQuery<reports.ReportPositiveNegativeDriversResponse>(
    ReportPositiveNegativeDriversQuery,
    {
      variables: {
        reportID: reportId,
      },
    },
  );

  const {
    loading: reportSummaryLoading,
    error: reportSummaryError,
    productVersionPqs,
  } = useReportSummary(reportId, {
    fetchPolicy: 'no-cache',
  });

  const {
    loading: comparisonLoading,
    error: comparisonError,
    data: comparisonData,
  } = useQuery<reports.ReportStackRankComparison>(
    ReportStackRankComparisonQuery,
    {
      variables: {
        reportID: reportId,
      },
    },
  );

  if (
    reportFlavorStructureLoading ||
    reportFlavorStructureError ||
    reportSummaryLoading ||
    reportSummaryError ||
    comparisonLoading ||
    comparisonError
  ) {
    return <LoadingScreen />;
  }

  return (
    <Box>
      <ComponentTitle>{t('aiLiking.title')}</ComponentTitle>
      <DataTable
        columns={columns(
          mapToTableRow(
            productNames,
            reportFlavorStructureResponse,
            productVersionPqs,
          ),
        )}
        data={mapToTableRow(
          productNames,
          reportFlavorStructureResponse,
          productVersionPqs,
        )}
        defaultSortAsc={false}
        customStyles={tableCustomStyles(theme)}
        striped
        conditionalRowStyles={hightlightedConditionalRowStyles}
      />
    </Box>
  );
};

export default CategoryInsightsTable;
